/**
 * Real reachability probe.
 *
 * `navigator.onLine` was the only connectivity signal the app had, and it
 * is not trustworthy for this: it reports whether the machine has *a*
 * network interface, not whether our server can be reached. Every one of
 * the dropped sync cycles recorded `online: true` while nothing reached
 * the server — the NIC was fine, the sockets were dead. So `onLine` is
 * kept as a QPL annotation only, and the coordinator gates on this probe.
 *
 * Deliberately a bare `fetch`, not the axios client:
 *   - it must bypass `baseURL` (`/healthz` is served at site root, outside
 *     `/api/v1`) and the QPL request interceptor, so probes don't pollute
 *     the dashboard with a per-attempt `http_call` flow;
 *   - it must not be retried by the transport-retry policy — the probe IS
 *     the retry loop;
 *   - it needs no credentials: `/healthz` is unauthenticated, and omitting
 *     them keeps a probe from touching session state.
 *
 * ANY HTTP response counts as reachable, including a 5xx. We are not
 * asking "is the backend healthy", only "did a socket complete a
 * round trip" — if it did, issuing the sync cycle is worthwhile and real
 * HTTP errors are the features' business.
 */

export const PROBE_PATH = '/healthz';
export const PROBE_TIMEOUT_MS = 5000;

export async function probeReachable(timeoutMs: number = PROBE_TIMEOUT_MS): Promise<boolean> {
  if (typeof fetch === 'undefined') return true; // no probe available — don't block sync
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
  const timer = setTimeout(() => controller?.abort(), timeoutMs);
  try {
    await fetch(PROBE_PATH, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
      signal: controller?.signal,
    });
    return true;
  } catch {
    // Network error, DNS failure, or our own timeout abort — either way
    // nothing completed a round trip.
    return false;
  } finally {
    clearTimeout(timer);
  }
}
